/* ShelterPage — /app/rifugio (index) & /app/rifugio/registra
   - If user has no shelter → shows registration form.
   - If pending → shows waiting state.
   - If verified → shows management dashboard with adoption dogs + donation missions.
   - If rejected → shows rejection reason + resubmit option.
*/
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HeartHandshake, MapPin, Phone, Mail, Globe, PawPrint,
  Clock, CheckCircle2, XCircle, Send, Loader2, Plus, ArrowRight, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { hapticTap } from "@/lib/haptics";

const DONATION_TYPES = [
  { id: "food", label: "Cibo", icon: "🥫" },
  { id: "water", label: "Acqua", icon: "💧" },
  { id: "money", label: "Donazioni", icon: "💶" },
  { id: "toys", label: "Giochi", icon: "🎾" },
  { id: "meds", label: "Farmaci", icon: "💊" },
  { id: "blankets", label: "Coperte", icon: "🛏️" },
  { id: "other", label: "Altro", icon: "✨" },
];

export default function ShelterPage() {
  const [shelter, setShelter] = useState(undefined); // undefined = loading, null = none
  const [missions, setMissions] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/shelter/me");
      setShelter(data.shelter || null);
      if (data.shelter?.status === "verified") {
        const m = await api.get("/shelter/missions/active");
        setMissions((m.data.items || []).filter((mi) => mi.owner_user_id === data.shelter.owner_user_id));
      }
    } catch (e) {
      console.warn("shelter load", formatErr(e?.response?.data?.detail));
      setShelter(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (shelter === undefined) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF7A1A]" />
      </div>
    );
  }

  if (!shelter) return <ShelterRegisterForm onDone={load} />;
  if (shelter.status === "pending") return <ShelterPending shelter={shelter} />;
  if (shelter.status === "rejected") return <ShelterRejected shelter={shelter} onRetry={load} />;
  return <ShelterDashboard shelter={shelter} missions={missions} reload={load} />;
}

/* ---------- Registration form ---------- */

function ShelterRegisterForm({ onDone }) {
  const [f, setF] = useState({
    name: "", city: "", address: "", tax_code: "", phone: "", email: "",
    website: "", description: "", lat: null, lng: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const detectPos = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => set("lat", p.coords.latitude) || set("lng", p.coords.longitude),
      () => {},
      { timeout: 3000 },
    );
  };

  const submit = async () => {
    setErr(null);
    if (f.name.length < 2 || f.city.length < 2 || f.description.length < 20) {
      setErr("Compila almeno nome, città e una descrizione di 20+ caratteri.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/shelter/register", f);
      hapticTap();
      toast.success("Richiesta inviata! Ora tocca alla verifica ORIO.");
      onDone?.();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || "Errore invio richiesta.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="shelter-register-form">
      <div className="mb-4">
        <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">
          <HeartHandshake className="h-3 w-3" /> Rifugio ORIO
        </div>
        <h1 className="mt-1 font-display text-3xl leading-tight">Registra il tuo rifugio</h1>
        <p className="mt-2 text-[13.5px] text-[#4A4A4A] leading-relaxed">
          Compila il modulo. Dopo la verifica manuale (di solito entro 48h), il tuo rifugio apparirà sulla mappa e potrai
          pubblicare cani in cerca di adozione o stallo, oltre a lanciare richieste di aiuto (cibo, acqua, donazioni).
        </p>
      </div>

      <Field label="Nome del rifugio *">
        <input value={f.name} onChange={(e) => set("name", e.target.value)}
          placeholder="Es. Rifugio Amici Fedeli"
          data-testid="shelter-name" className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Città *">
          <input value={f.city} onChange={(e) => set("city", e.target.value)}
            placeholder="Milano" data-testid="shelter-city" className={inputCls} />
        </Field>
        <Field label="Codice fiscale / P.IVA">
          <input value={f.tax_code} onChange={(e) => set("tax_code", e.target.value)}
            placeholder="12345678901" data-testid="shelter-tax" className={inputCls} />
        </Field>
      </div>

      <Field label="Indirizzo">
        <input value={f.address} onChange={(e) => set("address", e.target.value)}
          placeholder="Via Torino 10" data-testid="shelter-addr" className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Telefono">
          <input value={f.phone} onChange={(e) => set("phone", e.target.value)}
            placeholder="+39 02 1234567" data-testid="shelter-phone" className={inputCls} />
        </Field>
        <Field label="Email">
          <input type="email" value={f.email} onChange={(e) => set("email", e.target.value)}
            placeholder="info@rifugio.it" data-testid="shelter-email" className={inputCls} />
        </Field>
      </div>

      <Field label="Sito web">
        <input value={f.website} onChange={(e) => set("website", e.target.value)}
          placeholder="https://..." className={inputCls} />
      </Field>

      <Field label="Descrizione * (chi siete, quanti cani, missione)">
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)}
          rows={5} placeholder="Piccolo rifugio no-profit nato nel 2018..."
          data-testid="shelter-desc" className={inputCls + " resize-none"} />
      </Field>

      <button
        onClick={detectPos}
        type="button"
        className="mt-1 mb-4 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#FF7A1A]"
      >
        <MapPin className="h-3.5 w-3.5" />
        {f.lat ? "Posizione rilevata ✓" : "Rileva posizione GPS"}
      </button>

      {err && <div className="mb-3 rounded-xl bg-[#FFEEDD] text-[#C25612] text-[13px] px-3 py-2">{err}</div>}

      <button
        onClick={submit}
        disabled={submitting}
        data-testid="shelter-submit"
        className="w-full h-12 rounded-full font-bold text-white text-[14px] inline-flex items-center justify-center gap-2"
        style={{
          background: submitting ? "#E5DECF" : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
          color: submitting ? "#9A9A9A" : "white",
          boxShadow: submitting ? "none" : "0 14px 30px -10px rgba(255,122,26,0.55)",
        }}
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {submitting ? "Invio…" : "Invia richiesta di verifica"}
      </button>
    </div>
  );
}

/* ---------- Pending state ---------- */

function ShelterPending({ shelter }) {
  return (
    <div className="max-w-lg mx-auto p-6 text-center" data-testid="shelter-pending">
      <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4"
        style={{ background: "linear-gradient(135deg,#FFF6E6,#FFE8CB)", border: "1px solid #F0D9B0" }}>
        <Clock className="h-8 w-8 text-[#C25612]" />
      </div>
      <h2 className="font-display text-2xl">In attesa di verifica</h2>
      <p className="mt-2 text-[13.5px] text-[#4A4A4A]">
        Un amministratore ORIO sta controllando i dati di <strong>{shelter.name}</strong>. Riceverai una notifica appena la verifica sarà completata.
      </p>
      <div className="mt-4 text-[12px] text-[#9A9A9A]">
        Richiesta inviata: {new Date(shelter.submitted_at).toLocaleDateString("it-IT")}
      </div>
    </div>
  );
}

/* ---------- Rejected state ---------- */

function ShelterRejected({ shelter, onRetry }) {
  const [showForm, setShowForm] = useState(false);
  if (showForm) return <ShelterRegisterForm onDone={onRetry} />;
  return (
    <div className="max-w-lg mx-auto p-6 text-center" data-testid="shelter-rejected">
      <div className="mx-auto h-20 w-20 rounded-full flex items-center justify-center mb-4"
        style={{ background: "#FFEEDD", border: "1px solid #F5B67A" }}>
        <XCircle className="h-8 w-8 text-[#C25612]" />
      </div>
      <h2 className="font-display text-2xl">Richiesta non approvata</h2>
      <p className="mt-2 text-[13.5px] text-[#4A4A4A]">
        <strong>Motivo:</strong> {shelter.rejection_reason || "Dati insufficienti"}
      </p>
      <button
        onClick={() => setShowForm(true)}
        className="mt-4 px-5 py-2.5 rounded-full font-bold text-white text-[13.5px]"
        style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}
      >
        Riprova
      </button>
    </div>
  );
}

/* ---------- Dashboard (verified) ---------- */

function ShelterDashboard({ shelter, missions, reload }) {
  const [showMissionForm, setShowMissionForm] = useState(false);
  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" data-testid="shelter-dashboard">
      <div className="rounded-3xl p-5 border relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #E8F4EA 0%, #C7E8CF 100%)",
          borderColor: "#A8D3B4",
        }}>
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white bg-[#2DBA60]">
          <CheckCircle2 className="h-3 w-3" /> Rifugio verificato
        </div>
        <h1 className="mt-2 font-display text-2xl">{shelter.name}</h1>
        <div className="mt-1 text-[13px] text-[#3A3A3A] inline-flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" /> {shelter.city}{shelter.address ? ` · ${shelter.address}` : ""}
        </div>
        <div className="mt-3 grid grid-cols-3 gap-3">
          {shelter.phone && <InfoTag icon={Phone} v={shelter.phone} />}
          {shelter.email && <InfoTag icon={Mail} v={shelter.email} />}
          {shelter.website && <InfoTag icon={Globe} v={shelter.website} />}
        </div>
      </div>

      {/* Adoption dogs quick action */}
      <div className="mt-4 rounded-2xl bg-white border border-[#F0EBE3] p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">Cani in adozione</div>
            <div className="font-display text-lg">Gestisci le schede</div>
          </div>
          <Link to="/app/cani" data-testid="shelter-manage-dogs"
            className="px-4 h-10 rounded-full inline-flex items-center gap-1.5 font-semibold text-[13px] text-white"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" }}>
            <PawPrint className="h-4 w-4" /> Aggiungi cane <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <p className="mt-2 text-[12.5px] text-[#7A7A7A]">
          Aggiungi le schede complete dei cani (foto, storia, carattere), poi attiva <em>adozione / stallo / riservato</em> dallo switch nella scheda cane.
        </p>
      </div>

      {/* Donation missions */}
      <div className="mt-4 rounded-2xl bg-white border border-[#F0EBE3] p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">Richieste alla community</div>
            <div className="font-display text-lg">Missioni rifugio</div>
          </div>
          <button
            onClick={() => setShowMissionForm(true)}
            data-testid="shelter-new-mission"
            className="h-10 px-4 rounded-full inline-flex items-center gap-1.5 font-semibold text-[13px] bg-[#FBF7F1] border border-[#F0EBE3]"
          >
            <Plus className="h-4 w-4" /> Nuova richiesta
          </button>
        </div>

        {missions.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#E5DECF] p-4 text-center text-[12.5px] text-[#7A7A7A]">
            Nessuna richiesta attiva. Chiedi alla community aiuto per <strong>cibo, acqua, farmaci o donazioni</strong>.
          </div>
        ) : (
          <div className="space-y-2">
            {missions.map((m) => (
              <MissionRow key={m.id} m={m} onClose={reload} />
            ))}
          </div>
        )}
      </div>

      {showMissionForm && (
        <MissionForm
          onClose={() => setShowMissionForm(false)}
          onCreated={() => { setShowMissionForm(false); reload(); }}
        />
      )}
    </div>
  );
}

function MissionRow({ m, onClose }) {
  const kind = DONATION_TYPES.find((t) => t.id === m.kind) || DONATION_TYPES[6];
  const closeMission = async () => {
    if (!window.confirm("Chiudere la richiesta?")) return;
    try {
      await api.delete(`/shelter/me/missions/${m.id}`);
      toast.success("Richiesta chiusa");
      onClose?.();
    } catch {
      toast.error("Errore");
    }
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-[#F0EBE3]">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center text-xl"
        style={{ background: "#FFF6E6" }}>{kind.icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold truncate">{m.title}</div>
        <div className="text-[11.5px] text-[#7A7A7A]">
          {kind.label}{m.target_amount ? ` · obiettivo ${m.target_amount}${m.unit || ""}` : ""}
        </div>
      </div>
      <button onClick={closeMission}
        className="h-9 w-9 rounded-full bg-[#FFEEDD] text-[#C25612] flex items-center justify-center">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

function MissionForm({ onClose, onCreated }) {
  const [kind, setKind] = useState("food");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [target_amount, setTargetAmount] = useState("");
  const [unit, setUnit] = useState("kg");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (title.length < 3) return toast.error("Titolo troppo breve");
    setBusy(true);
    try {
      await api.post("/shelter/me/missions", {
        kind, title, description,
        target_amount: target_amount ? Number(target_amount) : null,
        unit: unit || null,
      });
      hapticTap();
      onCreated?.();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[1200] bg-black/60 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] rounded-t-[28px] p-5 pb-8 max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <div className="font-display text-xl">Nuova richiesta</div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold mb-2">Tipo</div>
        <div className="flex gap-2 flex-wrap mb-4">
          {DONATION_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setKind(t.id)}
              data-testid={`mission-kind-${t.id}`}
              className="px-3 py-1.5 rounded-full text-[12px] font-semibold border"
              style={{
                background: kind === t.id ? "#FF7A1A" : "white",
                borderColor: kind === t.id ? "#FF7A1A" : "#F0EBE3",
                color: kind === t.id ? "white" : "#1A1A1A",
              }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <Field label="Titolo *">
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Es. Cerchiamo 20kg di crocchette adulti"
            className={inputCls} data-testid="mission-title" />
        </Field>
        <Field label="Descrizione">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
            className={inputCls + " resize-none"} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Obiettivo (opz.)">
            <input value={target_amount} onChange={(e) => setTargetAmount(e.target.value)} type="number"
              placeholder="20" className={inputCls} />
          </Field>
          <Field label="Unità">
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="kg / € / pz" className={inputCls} />
          </Field>
        </div>

        <button
          onClick={submit}
          disabled={busy}
          data-testid="mission-submit"
          className="w-full mt-4 h-12 rounded-full font-bold text-white text-[14px] inline-flex items-center justify-center gap-2"
          style={{
            background: busy ? "#E5DECF" : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            color: busy ? "#9A9A9A" : "white",
          }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Pubblica richiesta
        </button>
      </motion.div>
    </motion.div>
  );
}

/* ---------- Small primitives ---------- */

function Field({ label, children }) {
  return (
    <label className="block mb-3">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold mb-1">{label}</div>
      {children}
    </label>
  );
}

function InfoTag({ icon: Icon, v }) {
  return (
    <div className="rounded-lg bg-white/70 px-2.5 py-1.5 text-[11.5px] text-[#1A1A1A] inline-flex items-center gap-1.5">
      <Icon className="h-3 w-3 text-[#2DBA60]" />
      <span className="truncate">{v}</span>
    </div>
  );
}

const inputCls = "w-full rounded-xl px-3 py-2.5 text-[14px] bg-white border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] transition";
